import type { LeaveHistoryFilters } from '../types/leave';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Default leave history window is the current calendar year.
 * Backend requires fromDate and toDate; history has no status/type filters.
 */
export function createDefaultLeaveDateRange(now = new Date()): {
  fromDate: string;
  toDate: string;
} {
  const year = now.getFullYear();
  return {
    fromDate: `${year}-01-01`,
    toDate: `${year}-12-31`,
  };
}

export function createDefaultLeaveFilters(now = new Date()): LeaveHistoryFilters {
  return createDefaultLeaveDateRange(now);
}

export function isValidLeaveDate(value: string): boolean {
  if (!DATE_ONLY.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return (
    !Number.isNaN(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) === value
  );
}

export function validateLeaveDateRange(
  fromDate: string,
  toDate: string,
): string | null {
  if (!isValidLeaveDate(fromDate)) {
    return 'From date must use YYYY-MM-DD format.';
  }
  if (!isValidLeaveDate(toDate)) {
    return 'To date must use YYYY-MM-DD format.';
  }
  if (fromDate > toDate) {
    return 'The selected date range is invalid.';
  }
  return null;
}

export function normalizeLeaveFilters(input: {
  fromDate?: string;
  toDate?: string;
  page?: number;
  pageSize?: number;
}): {
  filters: LeaveHistoryFilters;
  page: number;
  pageSize: number;
} {
  const defaults = createDefaultLeaveDateRange();
  const fromDate = input.fromDate?.trim() || defaults.fromDate;
  const toDate = input.toDate?.trim() || defaults.toDate;
  const rangeError = validateLeaveDateRange(fromDate, toDate);
  if (rangeError) {
    throw Object.assign(new Error(rangeError), { code: 'INVALID_DATE_RANGE' });
  }

  const page = input.page ?? 1;
  const pageSize = input.pageSize ?? 20;
  if (!Number.isInteger(page) || page <= 0) {
    throw Object.assign(new Error('page must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }
  if (!Number.isInteger(pageSize) || pageSize <= 0 || pageSize > 100) {
    throw Object.assign(new Error('pageSize must be between 1 and 100'), {
      code: 'VALIDATION_ERROR',
    });
  }

  return {
    filters: { fromDate, toDate },
    page,
    pageSize,
  };
}
